import { API_URL } from '../config/apiConfig';

const SESSION_KEY = 'booking_lead_session_id';

let lastTrackedTimestamp = 0;
let lastTrackedKey = '';


export const getBookingSessionId = () => {
  if (typeof window === 'undefined') return '';
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Helper: Resolve logged-in customer info (excluding admin)
const resolveUserInfo = () => {
  let name = '';
  let phone = '';
  let email = '';
  let userId = null;

  if (typeof window === 'undefined') return { userId, name, phone, email };

  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const parsed = JSON.parse(userStr);
      const u = parsed.user || parsed.data || parsed;
      if (u.role === 'admin' || parsed.role === 'admin') {
        return { userId: null, name: '', phone: '', email: '' };
      }
      userId = u._id || u.id || userId;
      name = u.name || u.fullName || u.username || name;
      phone = u.phone || u.mobile || phone;
      email = u.email || email;
    } catch (e) {}
  }

  if (!phone) phone = localStorage.getItem('userMobile') || '';
  if (!name) name = localStorage.getItem('userName') || '';
  if (!email) email = localStorage.getItem('userEmail') || '';

  return { userId, name, phone: String(phone || '').trim(), email };
};

export const trackAbandonedBooking = async (bookingDetails = {}) => {
  try {
    // 1. Ignore if browsing admin panel
    if (typeof window !== 'undefined' && window.location && window.location.pathname.startsWith('/admin')) {
      return;
    }

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isInBookingFlow =
      currentPath.includes('/booking/car-selection') ||
      currentPath.includes('/booking/customer-details') ||
      currentPath.includes('/booking/payment') ||
      currentPath.includes('/cabs') ||
      currentPath.includes('/car-selection');

    // 2. Track only when user searches or is inside funnel
    if (!bookingDetails.isFindCabsClick && !isInBookingFlow) {
      return;
    }

    const sessionId = getBookingSessionId();
    const resolvedUser = resolveUserInfo();
    const merged = { ...bookingDetails };

    // Resolve Pickup Address
    let pickupAddr = '';
    if (typeof merged.pickupLocation === 'object' && merged.pickupLocation?.address) {
      pickupAddr = merged.pickupLocation.address;
    } else if (typeof merged.pickupLocation === 'string') {
      pickupAddr = merged.pickupLocation;
    } else {
      pickupAddr = merged.pickupAddress || merged.from || '';
    }

    // Resolve Drop Address
    let dropAddr = '';
    if (typeof merged.dropLocation === 'object' && merged.dropLocation?.address) {
      dropAddr = merged.dropLocation.address;
    } else if (typeof merged.dropLocation === 'string') {
      dropAddr = merged.dropLocation;
    } else {
      dropAddr = merged.dropAddress || merged.to || '';
    }

    // Validation: Pickup must not be empty or generic "India"
    if (!pickupAddr || pickupAddr.trim().length < 3 || pickupAddr.trim().toLowerCase() === 'india') {
      return;
    }

    // Validation: Phone must have at least 10 digits
    const rawPhone = String(merged.phone || merged.mobile || resolvedUser.phone || '').trim();
    const digitsOnly = rawPhone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return;
    }
    const finalPhone = digitsOnly.slice(-10);

    const parsedPrice = parseFloat(
      merged.totalAmount || merged.amount || merged.estimatedPrice || merged.selectedCab?.price || 0
    );

    const payload = {
      sessionId,
      userId: merged.userId || resolvedUser.userId || null,
      name: merged.name || resolvedUser.name || 'Customer Lead',
      phone: finalPhone,
      email: merged.email || resolvedUser.email || '',
      pickupLocation: {
        address: pickupAddr,
        lat: merged.pickupLocation?.lat || null,
        lng: merged.pickupLocation?.lng || null,
      },
      dropLocation: {
        address: dropAddr,
        lat: merged.dropLocation?.lat || null,
        lng: merged.dropLocation?.lng || null,
      },
      tripType: merged.tripType || merged.journeyType || 'one-way',
      pickupDate: merged.pickupDate || merged.travelDate || merged.date || '',
      pickupTime: merged.pickupTime || merged.travelTime || merged.time || '',
      returnDate: merged.returnDate || '',
      returnTime: merged.returnTime || '',
      selectedCab: merged.selectedCab || undefined,
      estimatedPrice: parsedPrice,
      distance: merged.distance ? `${merged.distance}` : '',
      duration: merged.duration || '',
      exitPage: merged.exitPage || currentPath || '/cabs',
    };

    const trackKey = `${sessionId}_${finalPhone}_${currentPath}_${pickupAddr}_${dropAddr}`;
    const now = Date.now();
    if (trackKey === lastTrackedKey && now - lastTrackedTimestamp < 2000) {
      return;
    }
    lastTrackedKey = trackKey;
    lastTrackedTimestamp = now;

    await fetch(`${API_URL}/abandoned-bookings/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('Error tracking abandoned booking lead:', error);
  }
};

export const markBookingConverted = async () => {
  try {
    const sessionId = sessionStorage.getItem(SESSION_KEY);
    const resolvedUser = resolveUserInfo();
    await fetch(`${API_URL}/abandoned-bookings/mark-converted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        userId: resolvedUser.userId,
        phone: resolvedUser.phone,
      }),
    });
    sessionStorage.removeItem(SESSION_KEY);
  } catch (error) {
    console.error('Error marking booking as converted:', error);
  }
};
